import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import log from "loglevel";
import {Dialog, Intent, NumericInput} from "@blueprintjs/core";
import {distanceFromLeftOptimizeDialog, DIALOG_SIZE, PRIMARY_TEXT_COLOR} from "./ManifestDialog.service";
import styled from "@emotion/styled";
import {DialogBody, DialogCloseIcon, DialogContainer, DialogHeader, DialogTitle, Footer} from "./OptimizeDialog";
import {Controller, ControllerRenderProps, useForm, useWatch} from "react-hook-form";
import {OpenDatePickerIcon, StartDateContainer} from "./TimePickerCommon";
import {DateTimePicker} from "@mui/x-date-pickers";
import {COLORS} from "../../../../constants/constants";
import {ActionButton, ConfirmationDialog} from "../../../common/ConfirmationDialog";
import dayjs from "dayjs";
import {
  CloseManifestMutationVariables,
  ManifestDriverEvent,
  ManifestDriverEventInput,
  Maybe,
  useClearManifestDriverEventsMutation,
  useCloseManifestMutation,
  useSaveManifestDriverEventsMutation
} from "../../../../generated/graphql";
import {AppToasterTopLeft} from "../../../../utils/toasters/toaster";

const isInvalidDateTime = (date: Date) => {
  return !dayjs(date, "YYYY-MM-DD hh:mm:ss").isValid();
};

const fixFloatTo1Decimal = (num: string) => {
  return parseFloat(num).toFixed(1);
};

export enum CloseManifestErrorMsg {
  INVALID_DATE_TIME = "Please enter a valid Start Time/End Time.",
  MISSING_START_DATE_TIME = "Please enter a Start Time before entering an End time.",
  END_TIME_BEFORE_START_TIME = "Please enter End Time greater than Start Time.",
  END_MILEAGE_LESS_THAN_START_MILEAGE = "Please enter Start Mileage less than End Mileage."
}

export interface CloseManifestDialogProps {
  manifestDriverId: number;
  manifestDriverName: string;
  closeManifestFunction: () => void;
  ampm?: boolean;
  initStartMileage: number | null | undefined;
  initEndMileage: number | null | undefined;
  manifestEvents: Maybe<Maybe<ManifestDriverEvent>[]> | undefined;
}
export interface CloseManifestDialogRef {
  open(): void;
  close(): void;
}

interface CloseManifestFormData {
  startMileage: string;
  endMileage: string;
  startTime: Date | null;
  endTime: Date | null;
}

enum ManifestDriverEventType {
  IN_SERVICE = 1,
  OUT_SERVICE = 2
}

export const getInitialDateTime = (manifestEvents: Maybe<Maybe<ManifestDriverEvent>[]> | undefined) => {
  const dateTimeMap = new Map<ManifestDriverEventType, Date | null>([
    [ManifestDriverEventType.IN_SERVICE, null],
    [ManifestDriverEventType.OUT_SERVICE, null]
  ]);
  if (!manifestEvents || manifestEvents.length === 0) return dateTimeMap;
  const inserviceEvents = manifestEvents.filter((event) => event!.eventTypeId === ManifestDriverEventType.IN_SERVICE);
  const outserviceEvents = manifestEvents.filter((event) => event!.eventTypeId === ManifestDriverEventType.OUT_SERVICE);
  if (inserviceEvents.length > 0) {
    const inserviceEvent = inserviceEvents.reduce(
      (prev, current) => (dayjs(prev!.startDateTime).isAfter(dayjs(current!.startDateTime)) ? current : prev),
      inserviceEvents[0]
    );
    dateTimeMap.set(ManifestDriverEventType.IN_SERVICE, dayjs(inserviceEvent!.startDateTime).toDate());
  }
  if (outserviceEvents.length > 0) {
    const outserviceEvent = outserviceEvents.reduce(
      (prev, current) => (dayjs(prev!.startDateTime).isBefore(dayjs(current!.startDateTime)) ? current : prev),
      outserviceEvents[0]
    );
    dateTimeMap.set(ManifestDriverEventType.OUT_SERVICE, dayjs(outserviceEvent!.startDateTime).toDate());
  }
  return dateTimeMap;
};

export const CloseManifestDialog = forwardRef<CloseManifestDialogRef, CloseManifestDialogProps>(
  (
    {
      manifestDriverId,
      manifestDriverName,
      closeManifestFunction,
      ampm = true,
      initStartMileage,
      initEndMileage,
      manifestEvents
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const {control, reset, getValues} = useForm<CloseManifestFormData>({
      defaultValues: {
        startMileage: "",
        endMileage: "",
        startTime: null,
        endTime: null
      }
    });

    const initValueStartEndDateTime = useRef<Map<ManifestDriverEventType, Date | null>>();

    const handleClose = () => setIsOpen(false);
    const handleOpen = useCallback(() => {
      setIsOpen(true);
      initValueStartEndDateTime.current = getInitialDateTime(manifestEvents);
      reset({
        startMileage: initStartMileage ? initStartMileage.toString() : "",
        endMileage: initEndMileage ? initEndMileage.toString() : "",
        startTime: initValueStartEndDateTime.current.get(ManifestDriverEventType.IN_SERVICE),
        endTime: initValueStartEndDateTime.current.get(ManifestDriverEventType.OUT_SERVICE)
      });
    }, [initEndMileage, initStartMileage, reset, manifestEvents]);

    const [errors, setErrors] = useState({
      mileageError: "",
      dateTimeError: ""
    });

    const [closeManifestMutation] = useCloseManifestMutation();
    const [saveManifestDriverEvent] = useSaveManifestDriverEventsMutation();
    const [clearManifestDriverEvents] = useClearManifestDriverEventsMutation();

    useImperativeHandle(
      ref,
      () => ({
        open: handleOpen,
        close: handleClose
      }),
      [handleOpen]
    );

    const startMileageValue = useWatch({
      control,
      name: "startMileage"
    });

    const endMileageValue = useWatch({
      control,
      name: "endMileage"
    });

    const startTimeValue = useWatch({
      control,
      name: "startTime"
    });

    const endTimeValue = useWatch({
      control,
      name: "endTime"
    });

    const handleCloseManifestWithData = useCallback(() => {
      setLoading(true);
      const closeManifestPayload: CloseManifestMutationVariables = {
        manifestDriverId: manifestDriverId,
        startMileage: startMileageValue.length > 0 ? fixFloatTo1Decimal(startMileageValue) : "",
        endMileage: endMileageValue.length > 0 ? fixFloatTo1Decimal(endMileageValue) : ""
      };
      closeManifestMutation({
        variables: {
          ...closeManifestPayload
        }
      })
        .then(async (r) => {
          if (r.errors) {
            log.error(r.errors);
            AppToasterTopLeft.show({
              intent: Intent.DANGER,
              icon: "asterisk",
              message: `Unable to close ${manifestDriverName}'s Manifest`
            });
            return;
          }

          //Clear existing manifest driver events if conditions met
          if (
            startTimeValue ||
            (!startTimeValue && initValueStartEndDateTime.current?.get(ManifestDriverEventType.IN_SERVICE))
          ) {
            await clearManifestDriverEvents({
              variables: {
                manifestDriverId,
                manifestDriverEventTypeId: ManifestDriverEventType.IN_SERVICE.toString()
              }
            });
          }
          if (
            endTimeValue ||
            (!endTimeValue && initValueStartEndDateTime.current?.get(ManifestDriverEventType.OUT_SERVICE))
          ) {
            await clearManifestDriverEvents({
              variables: {
                manifestDriverId,
                manifestDriverEventTypeId: ManifestDriverEventType.OUT_SERVICE.toString()
              }
            });
          }

          //Add new manifest driver events if conditions met
          const manifestDriverEvents: ManifestDriverEventInput[] = [];
          if (startTimeValue) {
            manifestDriverEvents.push({
              manifestDriverEventTypeId: ManifestDriverEventType.IN_SERVICE.toString(),
              startDateTime: dayjs(startTimeValue).format("YYYY-MM-DD HH:mm:ss")
            });
          }
          if (endTimeValue) {
            manifestDriverEvents.push({
              manifestDriverEventTypeId: ManifestDriverEventType.OUT_SERVICE.toString(),
              startDateTime: dayjs(endTimeValue).format("YYYY-MM-DD HH:mm:ss")
            });
          }

          if (manifestDriverEvents.length > 0) {
            await saveManifestDriverEvent({
              variables: {
                manifestDriverId,
                events: manifestDriverEvents
              }
            });
          }

          AppToasterTopLeft.show({
            intent: Intent.SUCCESS,
            icon: "tick",
            message: `Closed ${manifestDriverName}'s Manifest`
          });
          handleClose();
          closeManifestFunction();
        })
        .catch((reason) => {
          log.error(reason);
          AppToasterTopLeft.show({
            intent: Intent.DANGER,
            icon: "asterisk",
            message: `Unable to close ${manifestDriverName}'s Manifest`
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }, [
      clearManifestDriverEvents,
      closeManifestFunction,
      closeManifestMutation,
      endMileageValue,
      endTimeValue,
      manifestDriverId,
      manifestDriverName,
      saveManifestDriverEvent,
      startMileageValue,
      startTimeValue
    ]);

    const handleOpenConfirmationDialog = () => {
      setIsConfirmationDialogOpen(true);
    };

    const handleYesClick = () => {
      setIsConfirmationDialogOpen(false);
      handleCloseManifestWithData();
    };

    const handleNoClick = () => {
      setIsConfirmationDialogOpen(false);
      const startMile = getValues("startMileage");
      const endMile = getValues("endMileage");
      reset({
        startMileage: startMile,
        endMileage: endMile,
        startTime: initValueStartEndDateTime.current?.get(ManifestDriverEventType.IN_SERVICE),
        endTime: initValueStartEndDateTime.current?.get(ManifestDriverEventType.OUT_SERVICE)
      });
    };

    const handleClickClose = () => {
      const currentStartTime = getValues("startTime");
      const currentEndTime = getValues("endTime");
      const initialStartTime = initValueStartEndDateTime.current?.get(ManifestDriverEventType.IN_SERVICE);
      const initialEndTime = initValueStartEndDateTime.current?.get(ManifestDriverEventType.OUT_SERVICE);
      return (!currentStartTime && initialStartTime) || (!currentEndTime && initialEndTime)
        ? handleOpenConfirmationDialog()
        : handleCloseManifestWithData();
    };

    useEffect(() => {
      if (endMileageValue && startMileageValue && parseFloat(endMileageValue) < parseFloat(startMileageValue)) {
        setErrors((prev) => ({...prev, mileageError: CloseManifestErrorMsg.END_MILEAGE_LESS_THAN_START_MILEAGE}));
      } else {
        setErrors((prev) => ({...prev, mileageError: ""}));
      }
    }, [endMileageValue, startMileageValue]);

    useEffect(() => {
      if (startTimeValue && isInvalidDateTime(startTimeValue)) {
        setErrors((prev) => ({...prev, dateTimeError: CloseManifestErrorMsg.INVALID_DATE_TIME}));
      } else if (endTimeValue && isInvalidDateTime(endTimeValue)) {
        setErrors((prev) => ({...prev, dateTimeError: CloseManifestErrorMsg.INVALID_DATE_TIME}));
      } else if (startTimeValue && endTimeValue && dayjs(endTimeValue).isBefore(dayjs(startTimeValue))) {
        setErrors((prev) => ({...prev, dateTimeError: CloseManifestErrorMsg.END_TIME_BEFORE_START_TIME}));
      } else if (!startTimeValue && endTimeValue) {
        setErrors((prev) => ({...prev, dateTimeError: CloseManifestErrorMsg.MISSING_START_DATE_TIME}));
      } else {
        setErrors((prev) => ({...prev, dateTimeError: ""}));
      }
    }, [startTimeValue, endTimeValue]);

    const isErrors = useMemo(() => {
      return Object.values(errors).some((error) => error);
    }, [errors]);

    const numericInput = useCallback(
      ({
        field: {value, onChange, name}
      }: {
        field: ControllerRenderProps<CloseManifestFormData, "endMileage" | "startMileage">;
      }) => {
        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          if (/^(\d+\.?\d*|\.\d+)$/.test(e.target.value) || e.target.value === "") {
            onChange(e);
          }
        };
        return (
          <StyledNumericInput
            data-testid={`${name}-numeric-input`}
            value={value}
            onChange={handleChange}
            allowNumericCharactersOnly={true}
            buttonPosition={"none"}
            placeholder="ex. 12345"
          />
        );
      },
      []
    );

    const dateInput = useCallback(
      ({
        field: {value, onChange, ref, name}
      }: {
        field: ControllerRenderProps<CloseManifestFormData, "startTime" | "endTime">;
      }) => {
        return (
          <StartDateContainer>
            <StyledDateTimePicker
              data-testid={`${name}-date-input`}
              value={value}
              onChange={onChange}
              slots={{
                openPickerIcon: OpenDatePickerIcon
              }}
              inputRef={ref}
              ampm={ampm}
            />
          </StartDateContainer>
        );
      },
      [ampm]
    );

    return (
      <StyledDialog isOpen={isOpen} distanceFromLeft={distanceFromLeftOptimizeDialog()} canOutsideClickClose={false}>
        <DialogContainer>
          <DialogHeader>
            <DialogTitle data-testid="dialog-title">CLOSE MANIFEST INFORMATION</DialogTitle>
            <DialogCloseIcon minimal data-testid="dialog-close-icon" onClick={handleClose} icon="cross" />
          </DialogHeader>
          <DialogBody gap={10}>
            <Item>
              <ItemLabel>Start Mileage</ItemLabel>
              <Controller render={numericInput} control={control} name={"startMileage"} />
            </Item>
            <Item>
              <ItemLabel>End Mileage</ItemLabel>
              <Controller render={numericInput} control={control} name={"endMileage"} />
            </Item>
            <Item>
              <ItemLabel>Start Time</ItemLabel>
              <Controller render={dateInput} control={control} name={"startTime"} />
            </Item>
            <Item>
              <ItemLabel>End Time</ItemLabel>
              <Controller render={dateInput} control={control} name={"endTime"} />
            </Item>
            <Item>
              {isErrors &&
                Object.values(errors).map(
                  (error, index) =>
                    error && (
                      <p key={index} style={{color: "red", margin: 0}}>
                        {error}
                      </p>
                    )
                )}
            </Item>
          </DialogBody>
          <Footer>
            <ActionButton data-testid="dialog-cancel-button" onClick={handleClose}>
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleClickClose}
              data-testid="dialog-action-button"
              disabled={isErrors || loading}
              style={{
                color: "white",
                background: isErrors ? "#d9d9d9" : "linear-gradient(180deg, #214A89 0%, #14305A 100%)",
                whiteSpace: "nowrap",
                padding: "6px 16px"
              }}
              loading={loading}
            >
              Close Manifest
            </ActionButton>
          </Footer>
        </DialogContainer>
        <ConfirmationDialog
          testIdPrefix={"closeManifest-confirmation"}
          isVisible={isConfirmationDialogOpen}
          header={"Remove start or end time?"}
          body={"Are you sure you want to remove the manifests start and/or end time?"}
          yesText={"Yes"}
          noText={"Cancel"}
          yesTextColor={"#32A467"}
          handleYesClicked={handleYesClick}
          handleNoClicked={handleNoClick}
          handleClose={handleNoClick}
        />
      </StyledDialog>
    );
  }
);

CloseManifestDialog.displayName = "CloseManifestDialog";

export const StyledDialog = styled(Dialog)<{distanceFromLeft: number}>`
  position: absolute;
  left: ${(props: any) => props.distanceFromLeft && `${props.distanceFromLeft}px`};
  width: ${DIALOG_SIZE}px;
  &.bp5-dialog {
    background-color: #fcfcfc;
    border-radius: 0;
    border-radius: 16px;
    font-family: Roboto, sans-serif;
  }
`;

const Item = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ItemLabel = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${PRIMARY_TEXT_COLOR};
`;

const StyledDateTimePicker = styled(DateTimePicker)<{isValid?: boolean}>`
  width: 270px;

  .MuiInputBase-root {
    font-size: 14px !important;
    width: 100%;
    height: 32px !important;
    margin: 0 !important;
    background-color: ${COLORS.WHITE} !important;

    .MuiInputBase-input {
      padding: 8px !important;
      padding-right: 0 !important;
    }

    .MuiOutlinedInput-notchedOutline {
      border: ${(props) => (props.isValid !== false ? "solid 1px #dedede !important" : "solid 1px #cd4246 !important")};
    }

    ::placeholder {
      color: ${COLORS.COLOR_PLACE_HOLDER} !important;
    }
  }

  svg {
    font-size: 20px;
  }

  button {
    padding: 4px !important;
    margin: 0 2px 0 0 !important;
  }
`;

const StyledNumericInput = styled(NumericInput)`
  width: fit-content;

  .bp5-input {
    width: 184px;
    height: 32px;
    border-radius: 4px;
    border: solid 1px #dedede !important;
    box-shadow: none;
    padding: 0 6px;

    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    ::placeholder {
      color: ${COLORS.COLOR_PLACE_HOLDER};
    }
  }

  .bp5-tag {
    margin-right: 12px;
  }
`;
